import { createRouter, createWebHistory } from 'vue-router';
import LandingPage from '../components/LandingPage.vue';
import FutGameSearch from '../components/FutGameSearch.vue';
import BasketballGameSearch from '../components/BasketballGameSearch.vue';
import TextDisplay from '@/components/TextDisplay.vue';
import MethodologyPage from '@/components/MethodologyPage.vue';
import LeaguePage from '@/components/LeaguePage.vue';

const routes = [
  {
    path: '/',
    name: 'LandingPage',
    component: LandingPage,
    meta: {
      title: 'BallerAlgo - Explore AI-Driven Sports Predictions',
      metaTags: [
        {
          name: 'description',
          content: 'Discover BallerAlgo, your premier platform for AI-driven sports predictions. Analyze sports events and plan your betting strategies with enhanced insight. Access AI betting tips with BallerAlgo.'
        },
        {
          property: 'og:title',
          content: 'BallerAlgo - Explore AI-Driven Sports Predictions'
        },
        {
          property: 'og:description',
          content: 'Utilize advanced AI technology for comprehensive sports predictions. Dive into detailed analysis of betting strategies, see helpful betting tips for football, soccer, basketball, and your favorite sports.'
        },
        {
          property: 'og:type',
          content: 'website'
        },
        {
          property: 'og:url',
          content: 'https://www.balleralgo.com'
        },
        {
          property: 'og:image',
          content: 'https://www.balleralgo.com/images/ai-sports-prediction.png'
        },
        {
          property: 'og:site_name',
          content: 'BallerAlgo'
        },
        {
          property: 'og:locale',
          content: 'en_US'
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:title',
          content: 'BallerAlgo - Explore AI-Driven Sports Predictions'
        },
        {
          name: 'twitter:description',
          content: 'Your go-to source for AI-powered sports predictions. Gain insights into football odds and plan your bets with confidence. Examine AI football, soccer, and basketball predictions.'
        },
        {
          name: 'twitter:image',
          content: 'https://www.balleralgo.com/images/ai-sports-twitter.png'
        },
        {
          name: 'robots',
          content: 'index, follow'
        },
        {
          name: 'author',
          content: 'BallerAlgo, Inc.'
        },
        {
          name: 'keywords',
          content: 'football, soccer, sports, odds, betting, betting tips, prediction, sports predictions, AI, AI sports predictions, soccer predictions, uefa predictions, premier league predictions, turkish super league predictions'
        }
      ]
    }
  },
  {
    path: '/football/league/:leagueSlug',
    name: 'football-league',
    component: LeaguePage,
    props: true,
    meta: {
      title: 'Football League Predictions - BallerAlgo',
      metaTags: [
        {
          name: 'description',
          content: 'Browse league-specific football predictions, featured matches, standings, and upcoming fixtures on BallerAlgo.'
        }
      ]
    }
  },
  {
    path: '/football/:gameId?',
    name: 'football',
    component: FutGameSearch,
    props: true,
    meta: {
      title: 'AI Football (Soccer) Predictions - Analyze Match Outcomes',
      metaTags: [
        {
          name: 'description',
          content: 'Access AI-driven predictions for football matches. Examine potential outcomes and odds to strategize your bets effectively. Get betting tips and predictions from AI.'
        },
        {
          property: 'og:title',
          content: 'AI Football (Soccer) Predictions - Analyze Match Outcomes'
        },
        {
          property: 'og:description',
          content: 'Explore in-depth AI analysis for each football match. Understand the odds and make informed decisions with BallerAlgo. Utilize AI for football and soccer predictions.'
        },
        {
          property: 'og:type',
          content: 'article'
        },
        {
          property: 'og:url',
          content: 'https://www.balleralgo.com/football'
        },
        {
          property: 'og:image',
          content: 'https://www.balleralgo.com/images/football-analysis.png'
        },
        {
          name: 'keywords',
          content: 'football, sports, odds, betting, prediction, sports predictions, AI, AI sports predictions, soccer predictions, uefa predictions, premier league predictions, turkish super league predictions'
        }
      ]
    }
  },
  {
    path: '/basketball/:gameId?',
    name: 'basketball',
    component: BasketballGameSearch,
    props: true,
    meta: {
      title: 'AI Basketball Predictions - Analyze Match Outcomes',
      metaTags: [
        {
          name: 'description',
          content: 'Access AI-driven predictions for basketball matches. Examine potential outcomes and odds to strategize your bets effectively. Your source for AI-generated basketball predictions and betting tips.'
        },
        {
          property: 'og:title',
          content: 'AI Basketball Predictions - Analyze Match Outcomes'
        },
        {
          property: 'og:description',
          content: 'Explore in-depth AI analysis for each basketball game. Understand the odds and make informed decisions with BallerAlgo. Utilize AI for basketball predictions.'
        },
        {
          property: 'og:type',
          content: 'article'
        },
        {
          property: 'og:url',
          content: 'https://www.balleralgo.com/basketball'
        },
        {
          property: 'og:image',
          content: 'https://www.balleralgo.com/images/basketball-predictions.png'
        },
        {
          name: 'keywords',
          content: 'basketball, sports, odds, betting, prediction, sports predictions, AI, AI sports predictions, nba, euroleague, basketball predictions, euroleague predictions, nba predictions, turkish basketball league predictions'
        }
      ]
    }
  },
  {
    path: '/about',
    name: 'about',
    component: TextDisplay,
    props: { textName: 'about' },
    meta: {
      title: 'About BallerAlgo',
      metaTags: [
        {
          name: 'description',
          content: 'Learn what BallerAlgo publishes, who the site is for, and how our AI-assisted football prediction coverage is organized.'
        }
      ]
    }
  },
  {
    path: '/how-it-works',
    name: 'how-it-works',
    component: TextDisplay,
    props: { textName: 'how-it-works' },
    meta: {
      title: 'How BallerAlgo Works',
      metaTags: [
        {
          name: 'description',
          content: 'See how BallerAlgo turns football data into featured match rankings, promoted picks, and readable prediction pages.'
        }
      ]
    }
  },
  {
    path: '/methodology',
    name: 'methodology',
    component: MethodologyPage,
    meta: {
      title: 'BallerAlgo-Algorithm Methodology Paper',
      metaTags: [
        {
          name: 'description',
          content: 'Read the detailed methodology paper for balleralgo-algorithm, including temporal feature engineering, target training, publication logic, and featured ranking criteria.'
        }
      ]
    }
  },
  {
    path: '/leagues-we-cover',
    name: 'leagues-we-cover',
    component: TextDisplay,
    props: { textName: 'leagues-we-cover' },
    meta: {
      title: 'Leagues We Cover',
      metaTags: [
        {
          name: 'description',
          content: 'Browse the football leagues currently covered by BallerAlgo and understand the scope of our upcoming match predictions.'
        }
      ]
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: TextDisplay,
    props: { textName: 'contact' },
    meta: {
      title: 'Contact BallerAlgo',
      metaTags: [
        {
          name: 'description',
          content: 'Contact BallerAlgo for editorial, privacy, or partnership questions related to our football prediction platform.'
        }
      ]
    }
  },
  {
    path: '/conditions/:textName',
    name: 'text-display',
    component: TextDisplay,
    props: true,
    meta: {
      title: 'Terms and Conditions - Understand Your Rights',
      metaTags: [
        {
          name: 'description',
          content: 'Read the terms and conditions at BallerAlgo to know your rights and how to use our AI sports prediction tools responsibly.'
        },
        {
          property: 'og:title',
          content: 'Terms and Conditions - Understand Your Rights'
        },
        {
          property: 'og:description',
          content: 'Familiarize yourself with the legal aspects of using BallerAlgo for making informed sports betting decisions.'
        },
        {
          property: 'og:type',
          content: 'article'
        },
        {
          property: 'og:url',
          content: 'https://www.balleralgo.com/conditions'
        },
        {
          property: 'og:image',
          content: 'https://www.balleralgo.com/images/terms-conditions.png'
        }
      ]
    }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

const SITE_URL = 'https://www.balleralgo.com';

function buildCanonicalUrl(route) {
  const normalizedPath = route.path === '/' ? '' : route.path;
  return `${SITE_URL}${normalizedPath}`;
}

router.beforeEach((to, from, next) => {
  const nearestWithTitle = to.matched.slice().reverse().find((r) => r.meta && r.meta.title);
  const nearestWithMeta = to.matched.slice().reverse().find((r) => r.meta && r.meta.metaTags);
  const previousNearestWithMeta = from.matched.slice().reverse().find((r) => r.meta && r.meta.metaTags);

  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if (previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }

  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map((el) => el.parentNode.removeChild(el));

  const canonicalTag = document.createElement('link');
  canonicalTag.setAttribute('rel', 'canonical');
  canonicalTag.setAttribute('href', buildCanonicalUrl(to));
  canonicalTag.setAttribute('data-vue-router-controlled', '');
  document.head.appendChild(canonicalTag);

  if (!nearestWithMeta) return next();

  nearestWithMeta.meta.metaTags
    .map((tagDef) => {
      const tag = document.createElement('meta');

      Object.keys(tagDef).forEach((key) => {
        tag.setAttribute(key, tagDef[key]);
      });

      tag.setAttribute('data-vue-router-controlled', '');

      return tag;
    })
    .forEach((tag) => document.head.appendChild(tag));

  next();
});

export default router;
